import { PhoneIncoming } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { IHeldCall } from '../../types';
import { CallerIdentity } from './caller-identity';

interface HeldQueueProps {
    readonly queue: readonly IHeldCall[];
    /** Finish-first: picking is only enabled while no call is active. */
    readonly canPick: boolean;
    readonly onPick: (callId: string) => void;
}

/**
 * The hold queue — callers parked because every agent was busy, broadcast to
 * every online agent. Any agent can pick one up when free (finish-first).
 * Distinct amber "on hold" styling separates it from the green ringing queue.
 */
export function HeldQueue({ queue, canPick, onPick }: HeldQueueProps) {
    if (queue.length === 0) return null;

    return (
        <div
            data-slot="voice-held-queue"
            className="flex flex-col divide-y divide-white/40 border-b border-white/40 bg-warning-500/[0.06] dark:divide-white/[0.06] dark:border-white/[0.06] dark:bg-warning-500/[0.1]"
        >
            {queue.map((held) => {
                const caller = held.contact?.name ?? held.fromNumber;
                const line = held.channelIdentity.displayName ?? held.channelIdentity.identity;
                return (
                    <div
                        key={held.callId}
                        title={`On hold · ${line}`}
                        className="flex items-center gap-2 px-3 py-2"
                    >
                        <span className="size-2 shrink-0 rounded-full bg-warning-500" aria-hidden />
                        <CallerIdentity
                            name={held.contact?.name ?? null}
                            number={held.fromNumber}
                            size="sm"
                            className="min-w-0 flex-1 gap-2"
                        />
                        <span className="shrink-0 text-xs font-medium text-warning-600 dark:text-warning-400">
                            On hold
                        </span>
                        <Button
                            type="button"
                            size="icon-sm"
                            disabled={!canPick}
                            title={canPick ? undefined : 'Finish the current call first'}
                            aria-label={`Pick up call from ${caller}`}
                            className="rounded-full bg-warning-500 text-white shadow-sm hover:bg-warning-600"
                            onClick={() => onPick(held.callId)}
                        >
                            <PhoneIncoming className="size-3.5" />
                        </Button>
                    </div>
                );
            })}
        </div>
    );
}
